import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  FormControl,
  TextField,
  Button,
  Stack,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  Tooltip,
  IconButton,
  Snackbar,
  Alert,
  AlertTitle,
  useMediaQuery,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useLanguage } from '../contexts/LanguageContext';
import { encodeState, decodeState, saveToLocalStorage, loadFromLocalStorage, encodeFinalTvState } from '../utils/stateManager';
import {
  generateAllPartnerships,
  partnershipKey,
  partnershipsEqual,
  partnershipExists,
  findOptimalPartnershipCombination,
  selectRoundRobinPartnerships
} from './partnerships';
import TopActionBar from './TopActionBar';
import { styled } from '@mui/system';
import { FormatIcon } from './FormatIcons';
import TvIcon from '@mui/icons-material/Tv';
import ShareIcon from '@mui/icons-material/Share';
import EditIcon from '@mui/icons-material/Edit';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import { postTvState, TV_CHANNEL_NAME } from '../utils/tvSync';
import { extractPartnershipsFromGames, rollbackRoundReservations } from '../utils/roundTracking';
import { generateTournamentId, saveTournamentToFirebase, cleanupOldTournaments } from '../utils/firebaseSync';
import { database } from '../config/firebase';
import {
  createMexicanoTeams,
  createMixedMexicanoTeams,
  normalizeMexicanoPairing,
  reorderTiedMexicanoPlayers,
  reorderTiedMixedMexicanoPlayers,
} from '../utils/mexicano';

const EndTournamentButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.error.main,
  color: theme.palette.error.contrastText,
  '&:hover': {
    backgroundColor: theme.palette.error.dark,
  },
  '&:active': {
    backgroundColor: theme.palette.error.dark,
  },
  '&:focus': {
    backgroundColor: theme.palette.error.main,
  }
}));

const buildPartnershipPlayCountMap = (partnerships = []) => {
  const next = {};
  partnerships.forEach((pair) => {
    const key = partnershipKey(pair);
    next[key] = (next[key] || 0) + 1;
  });
  return next;
};

const buildBenchCountMap = (players = [], bench = []) => {
  const next = {};
  players.forEach((player) => {
    next[player] = 0;
  });
  bench.forEach((player) => {
    next[player] = (next[player] || 0) + 1;
  });
  return next;
};

function Tournament() {
  const { translate } = useLanguage();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { name } = useParams();
  const navigate = useNavigate();
  const [tournamentData, setTournamentData] = useState(null);
  const [currentGames, setCurrentGames] = useState([]);
  const [benchPlayers, setBenchPlayers] = useState([]);
  const [roundNumber, setRoundNumber] = useState(1);
  const [playerScores, setPlayerScores] = useState({});
  const [showEndConfirmation, setShowEndConfirmation] = useState(false);
  const [playCount, setPlayCount] = useState({});
  const [wins, setWins] = useState({});
  const [losses, setLosses] = useState({});
  const [draws, setDraws] = useState({});
  const [roundHistory, setRoundHistory] = useState([]); // completed rounds: [{ round, games, benchPlayers }]
  const [viewRoundNumber, setViewRoundNumber] = useState(1);
  const [previousRoundDraft, setPreviousRoundDraft] = useState(null);
  const previousRoundOriginalJsonRef = useRef(null);
  // Partnership tracking
  const [allPartnerships, setAllPartnerships] = useState([]);
  const [usedPartnerships, setUsedPartnerships] = useState([]);
  const [partnershipPlayCount, setPartnershipPlayCount] = useState({});
  const [benchCount, setBenchCount] = useState({});
  const [lastRoundPartnerships, setLastRoundPartnerships] = useState([]); // partnerships from previous round
  const tvChannelRef = useRef(null);
  const [timeLeft, setTimeLeft] = useState(null); // seconds
  const [roundEnded, setRoundEnded] = useState(false);
  const timerIntervalRef = useRef(null);
  // Share functionality
  const [showCopied, setShowCopied] = useState(false);
  const [shareError, setShareError] = useState(false);
  const [isCompletingRound, setIsCompletingRound] = useState(false);
  const [nextRoundReadyNumber, setNextRoundReadyNumber] = useState(null);
  const [highlightRoundHeader, setHighlightRoundHeader] = useState(false);
  const [previousRoundSavedRound, setPreviousRoundSavedRound] = useState(null);
  // Track last game scores for TV mode display
  const [lastGameScores, setLastGameScores] = useState({});
  // Firebase real-time sync
  const [firebaseId, setFirebaseId] = useState(null);
  const finishingRef = useRef(false);
  // Player status tracking (active/inactive)
  const [playerStatus, setPlayerStatus] = useState({});
  // Play count offset for late joiners (so they don't play every round to "catch up")
  const [playCountOffset, setPlayCountOffset] = useState({});
  // Edit roster modal state
  const [showEditRoster, setShowEditRoster] = useState(false);
  const [editRosterPlayers, setEditRosterPlayers] = useState([]);
  const [editRosterCourts, setEditRosterCourts] = useState(1);
  const [editRosterNewPlayer, setEditRosterNewPlayer] = useState('');
  const [editRosterNewGender, setEditRosterNewGender] = useState('male');
  const [editRosterError, setEditRosterError] = useState('');
  // Remove player confirmation dialog (first step: choose action)
  const [removePlayerConfirm, setRemovePlayerConfirm] = useState({ open: false, playerName: null });
  // Final confirmation for complete removal (second step)
  const [removePlayerFinalConfirm, setRemovePlayerFinalConfirm] = useState({ open: false, playerName: null });
  const [showArrangeFirstRound, setShowArrangeFirstRound] = useState(false);
  const [manualRoundGames, setManualRoundGames] = useState([]);
  const [manualRoundBench, setManualRoundBench] = useState([]);
  const [selectedManualSlot, setSelectedManualSlot] = useState(null);
  const [manualRoundError, setManualRoundError] = useState('');
  const [manualRoundSaved, setManualRoundSaved] = useState(false);
  const timerStorageKey = useMemo(
    () => `timer-${encodeURIComponent(name || '')}`,
    [name]
  );
  const buildEncodedState = useCallback(() => {
    if (!tournamentData) return null;
    const transientState = {
      name,
      firebaseId,
      players: tournamentData.players,
      playerStatus,
      courts: tournamentData.courts,
      format: tournamentData.format,
      mexicanoPairing: tournamentData.mexicanoPairing,
      mixedPairs: tournamentData.mixedPairs,
      playerGenders: tournamentData.playerGenders,
      roundType: tournamentData.roundType,
      targetPoints: tournamentData.targetPoints,
      roundMinutes: tournamentData.roundMinutes,
      games: currentGames,
      scores: playerScores,
      isComplete: tournamentData?.isComplete || false,
      playCount,
      playCountOffset,
      wins,
      losses,
      draws,
      roundHistory,
      round: roundNumber,
      allPartnerships,
      usedPartnerships,
      partnershipPlayCount,
      lastRoundPartnerships,
      benchCount,
      timeLeft,
      roundEnded,
      lastGameScores
    };
    return encodeState(transientState);
  }, [
    allPartnerships,
    benchCount,
    currentGames,
    draws,
    firebaseId,
    lastGameScores,
    lastRoundPartnerships,
    losses,
    name,
    partnershipPlayCount,
    playCount,
    playCountOffset,
    playerScores,
    playerStatus,
    roundEnded,
    roundNumber,
    roundHistory,
    timeLeft,
    tournamentData,
    usedPartnerships,
    wins
  ]);

  useEffect(() => {
    if ('BroadcastChannel' in window) {
      tvChannelRef.current = new BroadcastChannel(TV_CHANNEL_NAME);
    }
    return () => {
      tvChannelRef.current?.close();
    };
  }, []);

  const playWhistle = useCallback(() => {
    try {
      const audio = new Audio(require('../assets/sounds/referee-whistle-blow.mp3'));
      audio.play().catch((error) => {
        console.warn('Unable to play whistle sound', error);
      });
    } catch (error) {
      console.warn('Unable to play whistle', error);
    }
  }, []);

  const shuffleArray = useCallback((array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }, []);
  // Generate a new round. Supports multiple formats (default: Americano).
  // Pass overrides to avoid relying on async state updates after completing a round.
  const createNewRound = useCallback((overrides = {}) => {
    if (!tournamentData && !overrides.players) return;

    const effectiveScores = overrides.scores || playerScores;
    const effectivePlayCount = overrides.playCount || { ...playCount };
    const effectivePlayerStatus = overrides.playerStatus || playerStatus;
    const effectivePlayCountOffset = overrides.playCountOffset || playCountOffset;
    const effectivePairPlayCounts = overrides.pairPlayCounts || partnershipPlayCount;
    const effectiveBenchCount = overrides.benchCount || benchCount;
    const effectivePlayers = overrides.players || tournamentData.players;
    const effectivePlayerGenders = overrides.playerGenders || tournamentData.playerGenders || {};
    const effectiveUsedPartnerships = overrides.usedPartnerships !== undefined ? overrides.usedPartnerships : usedPartnerships;
    const effectiveLastRoundPartnerships = overrides.lastRoundPartnerships !== undefined
      ? overrides.lastRoundPartnerships
      : lastRoundPartnerships;
    const format = tournamentData?.format || 'americano';
    const mexicanoPairing = normalizeMexicanoPairing(tournamentData?.mexicanoPairing);

    // Helper to get effective play count including offset for fairness calculations
    const getEffectivePlayCount = (player) => 
      (effectivePlayCount[player] || 0) + (effectivePlayCountOffset[player] || 0);

    // Filter to only active players
    const activePlayers = effectivePlayers.filter(
      (p) => effectivePlayerStatus[p] !== 'inactive'
    );

    // Auto-adjust courts if not enough active players
    const maxPossibleCourts = Math.floor(activePlayers.length / 4);
    const effectiveCourts = overrides.courts !== undefined 
      ? overrides.courts 
      : Math.min(tournamentData.courts, maxPossibleCourts);
    
    if (effectiveCourts < 1 || activePlayers.length < 4) {
      console.warn('Not enough active players for a round');
      return;
    }

    const neededPlayers = effectiveCourts * 4; // players on courts this round
    const requiredPartnerships = effectiveCourts * 2; // teams of two

    const playerGenders = effectivePlayerGenders;
    const targetGenderPerCourt = tournamentData?.mixedPairs ? neededPlayers / 2 : null;

    // 1. Decide who plays: players with FEWEST playCount play, highest playCount get benched.
    // Only consider active players for bench calculation
    const previousBenchSource = overrides.previousBenchPlayers !== undefined
      ? overrides.previousBenchPlayers
      : benchPlayers;
    const prevBench = previousBenchSource.filter((p) => effectivePlayerStatus[p] !== 'inactive');
    let playingPlayers = [];
    let benchThisRound = [];

    // Adjusted games played come first. Historical bench counts only break ties,
    // so late arrivals do not have to catch up on benches from before they joined.
    const comparePlayPriority = (a, b) => {
      const playDiff = getEffectivePlayCount(a) - getEffectivePlayCount(b);
      if (playDiff !== 0) return playDiff;
      return (effectiveBenchCount[b] || 0) - (effectiveBenchCount[a] || 0);
    };
    const sortAscPlay = (arr) => [...arr].sort(
      (a, b) => comparePlayPriority(a, b) || a.localeCompare(b)
    );

    const buildLastRoundTeammateMap = (playersPool) => {
      const pool = new Set(playersPool);
      const teammateMap = {};
      effectiveLastRoundPartnerships.forEach((pair) => {
        const [p1, p2] = pair;
        if (pool.has(p1) && pool.has(p2)) {
          teammateMap[p1] = p2;
          teammateMap[p2] = p1;
        }
      });
      return teammateMap;
    };

    const countByGender = (list, gender) => list.filter((p) => playerGenders[p] === gender).length;

    const balanceMixedPlayers = (playingList, benchList) => {
      if (!targetGenderPerCourt) {
        return { playing: playingList, bench: benchList };
      }

      let playing = [...playingList];
      let bench = [...benchList];
      const target = targetGenderPerCourt;

      const swapPlayers = (fromGender, toGender) => {
        const playingCandidates = shuffleArray(playing.filter((p) => playerGenders[p] === fromGender))
          .sort((a, b) => getEffectivePlayCount(b) - getEffectivePlayCount(a));
        const benchCandidates = shuffleArray(bench.filter((p) => playerGenders[p] === toGender))
          .sort((a, b) => getEffectivePlayCount(a) - getEffectivePlayCount(b));

        while (countByGender(playing, fromGender) > target && benchCandidates.length) {
          const toBench = playingCandidates.shift();
          const toPlay = benchCandidates.shift();
          if (!toBench || !toPlay) break;

          playing = playing.filter((p) => p !== toBench);
          bench = bench.filter((p) => p !== toPlay);
          playing.push(toPlay);
          bench.push(toBench);
        }
      };

      if (countByGender(playing, 'male') > target) {
        swapPlayers('male', 'female');
      }

      if (countByGender(playing, 'female') > target) {
        swapPlayers('female', 'male');
      }

      return { playing, bench };
    };

    // Fairness-first baseline: always choose lowest effective play counts globally.
    // Tie-breakers still favor players who have benched more.
    playingPlayers = sortAscPlay([...activePlayers]).slice(0, neededPlayers);

    // Anti-fixed-groups guard: if we have prior bench + prior on-court groups,
    // force at least one player from each side when feasible.
    if (prevBench.length > 0 && neededPlayers > 1) {
      const prevBenchSet = new Set(prevBench);
      const prevOnCourt = activePlayers.filter((p) => !prevBenchSet.has(p));
      const prevOnCourtSet = new Set(prevOnCourt);
      const teammateMap = buildLastRoundTeammateMap(activePlayers);

      const pickCandidateToAdd = (pool, currentSet) => {
        const candidates = sortAscPlay(pool.filter((p) => !currentSet.has(p)));
        if (!candidates.length) return null;
        const nonTeammateCandidate = candidates.find((candidate) => {
          const teammate = teammateMap[candidate];
          if (!teammate || !currentSet.has(teammate)) return true;
          return !candidates.some((other) => other !== candidate && (!teammateMap[other] || !currentSet.has(teammateMap[other])));
        });
        return nonTeammateCandidate || candidates[0];
      };

      const pickPlayerToRemove = (lineup, requiredSet, protectedSet) => {
        const removable = lineup.filter((player) => {
          if (requiredSet.has(player)) return false;
          if (!protectedSet) return true;
          if (!protectedSet.has(player)) return true;
          return lineup.some((other) => other !== player && protectedSet.has(other));
        });

        if (!removable.length) return null;

        return [...removable].sort((a, b) => {
          const playDiff = getEffectivePlayCount(b) - getEffectivePlayCount(a);
          if (playDiff !== 0) return playDiff;
          const benchDiff = (effectiveBenchCount[a] || 0) - (effectiveBenchCount[b] || 0);
          if (benchDiff !== 0) return benchDiff;
          return a.localeCompare(b);
        })[0];
      };

      const ensureAtLeastOneFromGroup = (lineup, requiredGroupSet, protectedGroupSet) => {
        if (lineup.some((p) => requiredGroupSet.has(p))) return lineup;
        const lineupSet = new Set(lineup);
        const candidateToAdd = pickCandidateToAdd([...requiredGroupSet], lineupSet);
        if (!candidateToAdd) return lineup;
        const candidateToRemove = pickPlayerToRemove(lineup, requiredGroupSet, protectedGroupSet);
        if (!candidateToRemove) return lineup;

        return lineup.map((player) => (player === candidateToRemove ? candidateToAdd : player));
      };

      let mixedLineup = [...playingPlayers];
      mixedLineup = ensureAtLeastOneFromGroup(mixedLineup, prevBenchSet, prevOnCourtSet);
      mixedLineup = ensureAtLeastOneFromGroup(mixedLineup, prevOnCourtSet, prevBenchSet);
      mixedLineup = ensureAtLeastOneFromGroup(mixedLineup, prevBenchSet, prevOnCourtSet);

      // Keep deterministic/fair ordering after swaps.
      playingPlayers = sortAscPlay(mixedLineup);
    }

    // Bench = active players not selected to play (inactive players are excluded)
    const playSet = new Set(playingPlayers);
    benchThisRound = activePlayers.filter(p => !playSet.has(p));

    // Safety: if still over/under allocate (edge case), adjust
    if (playingPlayers.length < neededPlayers) {
      const need = neededPlayers - playingPlayers.length;
      const fillers = sortAscPlay(benchThisRound).slice(0, need);
      fillers.forEach(p => playSet.add(p));
      playingPlayers = [...playSet];
      benchThisRound = activePlayers.filter(p => !playSet.has(p));
    } else if (playingPlayers.length > neededPlayers) {
      // Trim excess by removing highest playCount among playing
      const trimmed = shuffleArray([...playingPlayers]).sort((a, b) => getEffectivePlayCount(b) - getEffectivePlayCount(a));
      const toBenchAgain = trimmed.slice(0, playingPlayers.length - neededPlayers);
      const toBenchSet = new Set(toBenchAgain);
      playingPlayers = playingPlayers.filter(p => !toBenchSet.has(p));
      benchThisRound = [...new Set([...benchThisRound, ...toBenchAgain])];
    }

    if (tournamentData.mixedPairs && targetGenderPerCourt) {
      const balanced = balanceMixedPlayers(playingPlayers, benchThisRound);
      playingPlayers = balanced.playing;
      benchThisRound = balanced.bench;
    }

    // Recheck the same play priority after lineup adjustments. In mixed games,
    // only swap players of the same gender to preserve balanced teams.
    if (benchThisRound.length > 0 && playingPlayers.length > 0) {
      let fairnessSwapped = true;
      while (fairnessSwapped) {
        fairnessSwapped = false;
        const playingByLowestPriority = [...playingPlayers].sort(
          (a, b) => comparePlayPriority(b, a)
        );
        for (const benchedPlayer of sortAscPlay(benchThisRound)) {
          const playerToBench = playingByLowestPriority.find((player) =>
            (!targetGenderPerCourt || playerGenders[benchedPlayer] === playerGenders[player])
            && comparePlayPriority(benchedPlayer, player) < 0
          );
          if (!playerToBench) continue;

          const benchIndex = benchThisRound.indexOf(benchedPlayer);
          const playingIndex = playingPlayers.indexOf(playerToBench);
          benchThisRound[benchIndex] = playerToBench;
          playingPlayers[playingIndex] = benchedPlayer;
          fairnessSwapped = true;
          break;
        }
      }
    }

    // 2. Update bench counts
    const nextBenchCount = { ...effectiveBenchCount };
    benchThisRound.forEach(p => { nextBenchCount[p] = (nextBenchCount[p] || 0) + 1; });
    setBenchCount(nextBenchCount);

    // Helper to test previous round usage
    const inLastRound = (p) => effectiveLastRoundPartnerships.some(lp => partnershipsEqual(lp, p));

    let games = [];
    let roundPartnerships = [];

    if (format === 'mexicano') {
      // Mexicano: rank players by points, group into courts of 4 (top court = top 4), then create teams within each court.
      // Within each court, games are based on current ranking: #1+#3 vs #2+#4 (same pattern for the next four, etc).
      const compareByRank = (a, b) => {
        const scoreDiff = (effectiveScores[b] || 0) - (effectiveScores[a] || 0);
        if (scoreDiff !== 0) return scoreDiff;
        // Use effective play count (includes offset for late joiners) for tiebreaking
        const playDiff = getEffectivePlayCount(a) - getEffectivePlayCount(b);
        if (playDiff !== 0) return playDiff;
        return a.localeCompare(b);
      };

      if (tournamentData.mixedPairs) {
        const rankedMales = [...playingPlayers].filter((p) => playerGenders[p] === 'male').sort(compareByRank);
        const rankedFemales = [...playingPlayers].filter((p) => playerGenders[p] === 'female').sort(compareByRank);
        const reorderedRanks = reorderTiedMixedMexicanoPlayers({
          rankedMales,
          rankedFemales,
          scores: effectiveScores,
          pairing: mexicanoPairing,
          pairPlayCounts: effectivePairPlayCounts,
          lastRoundPartnerships: effectiveLastRoundPartnerships,
        });
        const optimizedRankedMales = reorderedRanks.rankedMales;
        const optimizedRankedFemales = reorderedRanks.rankedFemales;

        for (let courtIndex = 0; courtIndex < effectiveCourts; courtIndex++) {
          const malePair = optimizedRankedMales.slice(courtIndex * 2, courtIndex * 2 + 2);
          const femalePair = optimizedRankedFemales.slice(courtIndex * 2, courtIndex * 2 + 2);
          if (malePair.length < 2 || femalePair.length < 2) break;

          const [team1Players, team2Players] = createMixedMexicanoTeams(
            malePair,
            femalePair,
            mexicanoPairing
          );

          games.push({
            courtId: courtIndex + 1,
            team1: { players: team1Players, score: null },
            team2: { players: team2Players, score: null }
          });
          roundPartnerships.push(team1Players, team2Players);
        }
      } else {
        const rankedPlaying = reorderTiedMexicanoPlayers({
          rankedPlayers: [...playingPlayers].sort(compareByRank),
          scores: effectiveScores,
          pairing: mexicanoPairing,
          pairPlayCounts: effectivePairPlayCounts,
          lastRoundPartnerships: effectiveLastRoundPartnerships,
        });

        for (let courtIndex = 0; courtIndex < effectiveCourts; courtIndex++) {
          const group = rankedPlaying.slice(courtIndex * 4, courtIndex * 4 + 4);
          if (group.length < 4) break;

          const [team1Players, team2Players] = createMexicanoTeams(group, mexicanoPairing);
          games.push({
            courtId: courtIndex + 1,
            team1: { players: team1Players, score: null },
            team2: { players: team2Players, score: null }
          });
          roundPartnerships.push(team1Players, team2Players);
        }
      }
    } else {
      // Americano: partnership algorithm

      // 3. Generate all candidate partnerships among playing players
      const candidatePartnerships = generateAllPartnerships(
        playingPlayers,
        playerGenders,
        tournamentData.mixedPairs
      );

      const buildRandomPairs = () => {
        if (tournamentData.mixedPairs) {
          const males = shuffleArray(playingPlayers.filter((p) => playerGenders[p] === 'male'));
          const females = shuffleArray(playingPlayers.filter((p) => playerGenders[p] === 'female'));
          const limit = Math.min(males.length, females.length, requiredPartnerships);
          const pairs = [];
          for (let i = 0; i < limit; i++) {
            pairs.push([males[i], females[i]]);
          }
          return pairs;
        }

        const shuffledPlayers = shuffleArray([...playingPlayers]);
        const pairs = [];
        for (let i = 0; i + 1 < shuffledPlayers.length && pairs.length < requiredPartnerships; i += 2) {
          pairs.push([shuffledPlayers[i], shuffledPlayers[i + 1]]);
        }
        return pairs;
      };

      const recentPartnerships = effectiveUsedPartnerships;

      // 4. Choose partnerships with the Americano selector
      roundPartnerships = selectRoundRobinPartnerships({
        candidatePartnerships,
        requiredPartnerships,
        usedPartnerships: effectiveUsedPartnerships,
        lastRoundPartnerships: effectiveLastRoundPartnerships,
        recentPartnerships,
        benchCount: effectiveBenchCount,
        pairPlayCounts: effectivePairPlayCounts
      });

      // 5. Fallback: random attempts avoiding last-round duplicates
      if (roundPartnerships.length < requiredPartnerships) {
        for (let attempt = 0; attempt < 12 && roundPartnerships.length < requiredPartnerships; attempt++) {
          const randomPairs = buildRandomPairs();
          let ok = true;
          for (const pair of randomPairs) {
            if (inLastRound(pair)) { ok = false; break; }
            if (roundPartnerships.length + randomPairs.length > requiredPartnerships) break;
          }
          if (ok && randomPairs.length >= requiredPartnerships) {
            roundPartnerships = randomPairs.slice(0, requiredPartnerships); break;
          }
        }
      }
      if (roundPartnerships.length < requiredPartnerships) {
        // Final fallback (allow duplicates from last round if unavoidable)
        const fallbackPairs = buildRandomPairs();
        if (fallbackPairs.length === requiredPartnerships) roundPartnerships = fallbackPairs;
      }

      // 6. Build games (two partnerships per court)
      const matchPartnerships = shuffleArray([...roundPartnerships]);
      for (let i = 0; i < matchPartnerships.length; i += 2) {
        if (i + 1 < matchPartnerships.length) {
          games.push({
            courtId: Math.floor(i / 2) + 1,
            team1: { players: matchPartnerships[i], score: null },
            team2: { players: matchPartnerships[i + 1], score: null }
          });
        }
      }
    }

    // Track newly used partnerships (both formats produce 2-player teams)
    const newlyUsed = roundPartnerships.filter(p => !partnershipExists(p, effectiveUsedPartnerships));
    setUsedPartnerships([...effectiveUsedPartnerships, ...newlyUsed]);

    // Commit state
    setCurrentGames(games);
    setBenchPlayers(benchThisRound);
    
    // Only increment round number if not regenerating current round
    if (!overrides.skipRoundIncrement) {
      setRoundNumber(prev => {
        const next = prev + 1;
        setViewRoundNumber(next);
        return next;
      });
    }
    setLastRoundPartnerships(roundPartnerships);
    const nextPartnershipPlayCount = { ...effectivePairPlayCounts };
    roundPartnerships.forEach((pair) => {
      const key = partnershipKey(pair);
      nextPartnershipPlayCount[key] = (nextPartnershipPlayCount[key] || 0) + 1;
    });
    setPartnershipPlayCount(nextPartnershipPlayCount);
  }, [
    benchCount,
    benchPlayers,
    lastRoundPartnerships,
    partnershipPlayCount,
    playCount,
    playCountOffset,
    playerScores,
    playerStatus,
    shuffleArray,
    tournamentData,
    usedPartnerships
  ]);

  useEffect(() => {
    finishingRef.current = false;
    const setupInitialTournament = (data) => {
      const normalized = {
        ...data,
        mixedPairs: Boolean(data.mixedPairs),
        playerGenders: data.playerGenders || {},
        mexicanoPairing: normalizeMexicanoPairing(data.mexicanoPairing),
        roundType: data.roundType || 'points',
        targetPoints: data.targetPoints || 21,
        roundMinutes: data.roundMinutes || 12
      };
      // Generate all possible partnerships
      const partnerships = generateAllPartnerships(normalized.players, normalized.playerGenders, normalized.mixedPairs);
      setAllPartnerships(partnerships);
      
      // Initialize player status (all active by default)
      const initialPlayerStatus = {};
      normalized.players.forEach(player => {
        initialPlayerStatus[player] = 'active';
      });
      setPlayerStatus(initialPlayerStatus);
      
      // Initialize bench count
      const initialBenchCount = {};
      normalized.players.forEach(player => {
        initialBenchCount[player] = 0;
      });
      setBenchCount(initialBenchCount);
      
      // Initialize play count
      const initialPlayCount = {};
      normalized.players.forEach(player => {
        initialPlayCount[player] = 0;
      });
      setPlayCount(initialPlayCount);

      // Initialize play count offset (empty for new tournaments, used for late joiners)
      setPlayCountOffset({});
      // Initialize match results (wins/losses/draws)
      const initialWins = {};
      const initialLosses = {};
      const initialDraws = {};
      normalized.players.forEach(player => {
        initialWins[player] = 0;
        initialLosses[player] = 0;
        initialDraws[player] = 0;
      });
      setWins(initialWins);
      setLosses(initialLosses);
      setDraws(initialDraws);
      
      // Initialize player scores
      setPlayerScores(normalized.players.reduce((acc, player) => ({ ...acc, [player]: 0 }), {}));
      
      // Generate first round
      const requiredPartnerships = normalized.courts * 2;
      const neededPlayers = normalized.courts * 4;

      // Determine which players play round 1 (may need to bench some)
      let playingPlayersRound1 = [...normalized.players];
      
      if (normalized.players.length > neededPlayers) {
        // Need to bench some players. Use randomized selection for round 1 so new tournaments
        // with same setup don't start identically.
        const sortedForBench = shuffleArray([...normalized.players]);

        if (normalized.mixedPairs) {
          const targetPerGender = Math.floor(neededPlayers / 2);
          const males = sortedForBench.filter((p) => normalized.playerGenders[p] === 'male');
          const females = sortedForBench.filter((p) => normalized.playerGenders[p] === 'female');

          if (males.length >= targetPerGender && females.length >= targetPerGender) {
            const selectedMales = males.slice(0, targetPerGender);
            const selectedFemales = females.slice(0, targetPerGender);
            const playingSet = new Set([...selectedMales, ...selectedFemales]);
            playingPlayersRound1 = [...playingSet];
          } else {
            // Fallback to generic selection if roster itself is imbalanced
            playingPlayersRound1 = sortedForBench.slice(0, neededPlayers);
          }
        } else {
          playingPlayersRound1 = sortedForBench.slice(0, neededPlayers);
        }
      }

      const initialGames = [];
      const roundPlayers = new Set();

      // Generate initial teams
      let firstRoundPartnerships = [];

      if (normalized.format === 'mexicano') {
        if (normalized.mixedPairs) {
          const shuffledMales = shuffleArray(
            playingPlayersRound1.filter((p) => normalized.playerGenders[p] === 'male')
          );
          const shuffledFemales = shuffleArray(
            playingPlayersRound1.filter((p) => normalized.playerGenders[p] === 'female')
          );

          for (let courtIndex = 0; courtIndex < normalized.courts; courtIndex++) {
            const malePair = shuffledMales.slice(courtIndex * 2, courtIndex * 2 + 2);
            const femalePair = shuffledFemales.slice(courtIndex * 2, courtIndex * 2 + 2);
            if (malePair.length < 2 || femalePair.length < 2) break;

            const [team1, team2] = createMixedMexicanoTeams(
              malePair,
              femalePair,
              normalized.mexicanoPairing
            );

            initialGames.push({
              courtId: courtIndex + 1,
              team1: { players: team1, score: null },
              team2: { players: team2, score: null }
            });
            firstRoundPartnerships.push(team1, team2);
            team1.forEach(p => roundPlayers.add(p));
            team2.forEach(p => roundPlayers.add(p));
          }
        } else {
          const shuffledPlaying = shuffleArray([...playingPlayersRound1]);

          for (let courtIndex = 0; courtIndex < normalized.courts; courtIndex++) {
            const group = shuffledPlaying.slice(courtIndex * 4, courtIndex * 4 + 4);
            if (group.length < 4) break;

            const [team1, team2] = createMexicanoTeams(group, normalized.mexicanoPairing);
            initialGames.push({
              courtId: courtIndex + 1,
              team1: { players: team1, score: null },
              team2: { players: team2, score: null }
            });
            firstRoundPartnerships.push(team1, team2);
            team1.forEach(p => roundPlayers.add(p));
            team2.forEach(p => roundPlayers.add(p));
          }
        }
      } else {
        const playingPartnerships = generateAllPartnerships(
          playingPlayersRound1,
          normalized.playerGenders,
          normalized.mixedPairs
        );
        const randomizedPartnerships = shuffleArray([...playingPartnerships]);

        firstRoundPartnerships = findOptimalPartnershipCombination(
          randomizedPartnerships,
          initialBenchCount,
          requiredPartnerships
        );

        if (firstRoundPartnerships.length < requiredPartnerships) {
          const shuffledPartnerships = shuffleArray([...playingPartnerships]);
          const fallback = findOptimalPartnershipCombination(
            shuffledPartnerships,
            initialBenchCount,
            requiredPartnerships
          );
          if (fallback.length > firstRoundPartnerships.length) {
            firstRoundPartnerships = fallback;
          }
        }

        if (normalized.mixedPairs && firstRoundPartnerships.length < requiredPartnerships) {
          const shuffledPlaying = shuffleArray([...playingPlayersRound1]);
          const males = shuffledPlaying.filter((p) => normalized.playerGenders[p] === 'male');
          const females = shuffledPlaying.filter((p) => normalized.playerGenders[p] === 'female');
          const mixedFallbackPairs = [];
          const limit = Math.min(males.length, females.length, requiredPartnerships);
          for (let i = 0; i < limit; i++) {
            mixedFallbackPairs.push([males[i], females[i]]);
          }
          if (mixedFallbackPairs.length >= requiredPartnerships) {
            firstRoundPartnerships = mixedFallbackPairs.slice(0, requiredPartnerships);
          }
        }

        const firstRoundMatchPartnerships = shuffleArray([...firstRoundPartnerships]);

        for (let i = 0; i < firstRoundMatchPartnerships.length; i += 2) {
          if (i + 1 < firstRoundMatchPartnerships.length) {
            const team1 = firstRoundMatchPartnerships[i];
            const team2 = firstRoundMatchPartnerships[i + 1];
            initialGames.push({
              courtId: Math.floor(i / 2) + 1,
              team1: { players: team1, score: null },
              team2: { players: team2, score: null }
            });
            team1.forEach(p => roundPlayers.add(p));
            team2.forEach(p => roundPlayers.add(p));
          }
        }
      }
      
      // Always derive bench from actual round participants (covers fallback/partial round edge cases)
      const finalBenchPlayers = normalized.players.filter(p => !roundPlayers.has(p));
      const updatedBenchCount = { ...initialBenchCount };
      finalBenchPlayers.forEach(p => {
        updatedBenchCount[p] = 1;
      });
      
      // Set all initial state
      setTournamentData(normalized);
      setCurrentGames(initialGames);
      setBenchPlayers(finalBenchPlayers);
      setRoundNumber(1);
      setViewRoundNumber(1);
      setRoundHistory([]);
      setPreviousRoundDraft(null);
      previousRoundOriginalJsonRef.current = null;
      setUsedPartnerships(firstRoundPartnerships);
      setLastRoundPartnerships(firstRoundPartnerships);
      setPartnershipPlayCount(buildPartnershipPlayCountMap(firstRoundPartnerships));
      setBenchCount(updatedBenchCount);
    };

    // Helper function to restore state from decoded data
    const restoreFromDecodedState = (decodedState) => {
      const normalizedState = {
        ...decodedState,
        mixedPairs: Boolean(decodedState.mixedPairs),
        playerGenders: decodedState.playerGenders || {},
        mexicanoPairing: normalizeMexicanoPairing(decodedState.mexicanoPairing),
        roundType: decodedState.roundType || 'points',
        targetPoints: decodedState.targetPoints || 21,
        roundMinutes: decodedState.roundMinutes || 12
      };
      // Persist identity before the first render/autosave, including StrictMode remounts.
      normalizedState.name = name;
      normalizedState.firebaseId = normalizedState.firebaseId || generateTournamentId();
      setFirebaseId(normalizedState.firebaseId);
      if (!decodedState.firebaseId) {
        saveToLocalStorage(name, normalizedState);
        if (Math.random() < 0.1) cleanupOldTournaments(30);
      }

      // Completed totals already include the last courts. Never reopen or count them twice.
      if (normalizedState.isComplete) {
        finishingRef.current = true;
        saveTournamentToFirebase(normalizedState.firebaseId, normalizedState);
        const encoded = encodeFinalTvState(normalizedState);
        postTvState(name, encoded, tvChannelRef);
        navigate(`/results/${encodeURIComponent(name)}?${new URLSearchParams({ state: encoded })}`, { replace: true });
        return;
      }

      // Restore tournament state
      setTournamentData(normalizedState);
      setAllPartnerships(normalizedState.allPartnerships || generateAllPartnerships(normalizedState.players, normalizedState.playerGenders, normalizedState.mixedPairs));
      setUsedPartnerships(normalizedState.usedPartnerships || []);
      const persistedGames = [
        ...(Array.isArray(normalizedState.roundHistory)
          ? normalizedState.roundHistory.flatMap((entry) => entry?.games || [])
          : []),
        ...(Array.isArray(normalizedState.games) ? normalizedState.games : []),
      ];
      setPartnershipPlayCount(
        normalizedState.partnershipPlayCount
        || buildPartnershipPlayCountMap(extractPartnershipsFromGames(persistedGames))
      );
      setBenchCount(normalizedState.benchCount || {});
      setLastGameScores(normalizedState.lastGameScores || {});
      setWins(normalizedState.wins || {});
      setLosses(normalizedState.losses || {});
      setDraws(normalizedState.draws || {});
      setRoundHistory(normalizedState.roundHistory || []);
      setViewRoundNumber(normalizedState.round || 1);
      setPreviousRoundDraft(null);
      previousRoundOriginalJsonRef.current = null;
      
      // Restore player status (default all to active if not present)
      const restoredPlayerStatus = normalizedState.playerStatus || {};
      normalizedState.players.forEach(p => {
        if (!restoredPlayerStatus[p]) restoredPlayerStatus[p] = 'active';
      });
      setPlayerStatus(restoredPlayerStatus);

      // Restore play count offset for late joiners
      setPlayCountOffset(normalizedState.playCountOffset || {});

      if (!normalizedState.games || normalizedState.games.length === 0) {
        setupInitialTournament(normalizedState);
      } else {
        // Use existing game state
        const restoredGames = normalizedState.games.map((game, index) => ({
          ...game,
          courtId: index + 1
        }));

        const shouldRebuildInitialRound =
          normalizedState.format === 'americano'
          && normalizedState.mixedPairs
          && (normalizedState.round || 1) === 1
          && restoredGames.length < normalizedState.courts;

        if (shouldRebuildInitialRound) {
          setupInitialTournament(normalizedState);
          return;
        }

        setCurrentGames(restoredGames);
        const activePlayers = restoredGames.flatMap(g => [
          ...g.team1.players,
          ...g.team2.players
        ]);
        const benchPlayersList = normalizedState.players.filter(
          p => !activePlayers.includes(p) && restoredPlayerStatus[p] !== 'inactive'
        );
        setBenchPlayers(benchPlayersList);
        setRoundNumber(normalizedState.round || 1);
        setViewRoundNumber(normalizedState.round || 1);
        setLastRoundPartnerships(
          normalizedState.lastRoundPartnerships || extractPartnershipsFromGames(restoredGames)
        );

        // Handle scores and play counts
        if (normalizedState.playerData) {
          const newScores = {};
          const newPlayCount = {};
          const newWins = {};
          const newLosses = {};
          const newDraws = {};
          Object.entries(normalizedState.playerData).forEach(([player, data]) => {
            newScores[player] = data.score;
            newPlayCount[player] = data.playCount;
            if (typeof data.wins === 'number') newWins[player] = data.wins;
            if (typeof data.losses === 'number') newLosses[player] = data.losses;
            if (typeof data.draws === 'number') newDraws[player] = data.draws;
          });
          setPlayerScores(newScores);
          setPlayCount(newPlayCount);
          // Back-compat: if playerData has no W/L/D, fall back to top-level maps.
          if (Object.keys(newWins).length) setWins(newWins);
          if (Object.keys(newLosses).length) setLosses(newLosses);
          if (Object.keys(newDraws).length) setDraws(newDraws);
        } else {
          setPlayerScores(normalizedState.scores || {});
          setPlayCount(normalizedState.playCount || {});
        }
      }
    };

    // Try URL state first (for shared links), then localStorage
    const searchParams = new URLSearchParams(window.location.search);
    const encodedState = searchParams.get('state');
    
    if (encodedState) {
      const decodedState = decodeState(encodedState);
      if (decodedState) {
        restoreFromDecodedState(decodedState);
        // Clear URL state and save to localStorage for future use
        window.history.replaceState({}, '', window.location.pathname);
        return;
      }
    }

    // Try localStorage if no URL state
    const localState = loadFromLocalStorage(name);
    if (localState) {
      restoreFromDecodedState(localState);
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name]);

  // All partnerships usage info (if needed later) can be derived: usedPartnerships.length === allPartnerships.length

  const handleCompleteGames = () => {
    if (isCompletingRound) return;

    if (!currentGames.every(game => game.team1.score !== null && game.team2.score !== null)) {
      alert(translate('tournament.enterScores'));
      return;
    }

    setIsCompletingRound(true);

    // Track last game scores for TV mode display
    const newLastGameScores = {};
    currentGames.forEach(game => {
      game.team1.players.forEach(p => {
        newLastGameScores[p] = game.team1.score;
      });
      game.team2.players.forEach(p => {
        newLastGameScores[p] = game.team2.score;
      });
    });
    setLastGameScores(newLastGameScores);

    // Commit scores & play counts for this just-finished round
    const newScores = { ...playerScores };
    const newPlayCount = { ...playCount };
    const newWins = { ...wins };
    const newLosses = { ...losses };
    const newDraws = { ...draws };

    currentGames.forEach(game => {
      // scores
      game.team1.players.forEach(p => {
        newScores[p] = (newScores[p] || 0) + game.team1.score;
      });
      game.team2.players.forEach(p => {
        newScores[p] = (newScores[p] || 0) + game.team2.score;
      });

      // match results (W/L/D)
      if (game.team1.score > game.team2.score) {
        game.team1.players.forEach((p) => {
          newWins[p] = (newWins[p] || 0) + 1;
        });
        game.team2.players.forEach((p) => {
          newLosses[p] = (newLosses[p] || 0) + 1;
        });
      } else if (game.team2.score > game.team1.score) {
        game.team2.players.forEach((p) => {
          newWins[p] = (newWins[p] || 0) + 1;
        });
        game.team1.players.forEach((p) => {
          newLosses[p] = (newLosses[p] || 0) + 1;
        });
      } else {
        [...game.team1.players, ...game.team2.players].forEach((p) => {
          newDraws[p] = (newDraws[p] || 0) + 1;
        });
      }

      // play counts
      [...game.team1.players, ...game.team2.players].forEach(p => {
        newPlayCount[p] = (newPlayCount[p] || 0) + 1;
      });
    });

    setPlayerScores(newScores);
    setPlayCount(newPlayCount);
    setWins(newWins);
    setLosses(newLosses);
    setDraws(newDraws);

    // Snapshot completed round for navigation/editing
    const roundSnapshotGames = currentGames.map((g) => ({
      ...g,
      team1: { ...g.team1, players: [...g.team1.players] },
      team2: { ...g.team2, players: [...g.team2.players] }
    }));
    const roundSnapshotBench = [...benchPlayers];
    setRoundHistory((prev) => [...prev, { round: roundNumber, games: roundSnapshotGames, benchPlayers: roundSnapshotBench }]);

    // Always continue to next round (avoid relying on async state updates)
    createNewRound({ scores: newScores, playCount: newPlayCount });

    setNextRoundReadyNumber(roundNumber + 1);
    setHighlightRoundHeader(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(() => setIsCompletingRound(false), 350);
  };

  useEffect(() => {
    if (!highlightRoundHeader) return undefined;

    const timer = setTimeout(() => {
      setHighlightRoundHeader(false);
    }, 1200);

    return () => clearTimeout(timer);
  }, [highlightRoundHeader]);

  const handleScoreChange = (courtIndex, team, value) => {
    const roundType = tournamentData?.roundType || 'points';
    const maxPoints = tournamentData?.targetPoints || 21;
    const newGames = [...currentGames];
    const game = newGames[courtIndex];
    const parsedScore = parseInt(value, 10);
    const score = Number.isNaN(parsedScore)
      ? null
      : Math.min(Math.max(parsedScore, 0), roundType === 'points' ? maxPoints : parsedScore);

    if (score === null) {
      if (roundType === 'points') {
        game.team1.score = null;
        game.team2.score = null;
      } else if (team === 1) {
        game.team1.score = null;
      } else {
        game.team2.score = null;
      }

      setCurrentGames(newGames);
      return;
    }
    
    if (roundType === 'points') {
      if (team === 1) {
        game.team1.score = score;
        game.team2.score = Math.max(0, maxPoints - score);
      } else {
        game.team2.score = score;
        game.team1.score = Math.max(0, maxPoints - score);
      }
    } else {
      if (team === 1) {
        game.team1.score = score;
      } else {
        game.team2.score = score;
      }
    }
    
    setCurrentGames(newGames);
  };

  const cloneGames = useCallback((games) => {
    return (games || []).map((g) => ({
      ...g,
      team1: { ...g.team1, players: [...g.team1.players] },
      team2: { ...g.team2, players: [...g.team2.players] }
    }));
  }, []);

  const getManualSlotKey = useCallback((slot) => {
    if (!slot) return '';
    if (slot.type === 'bench') return `bench:${slot.index}`;
    return `court:${slot.courtIndex}:${slot.team}:${slot.playerIndex}`;
  }, []);

  const getPlayerFromManualSlot = useCallback((games, bench, slot) => {
    if (!slot) return null;
    if (slot.type === 'bench') return bench[slot.index] || null;
    return games?.[slot.courtIndex]?.[slot.team]?.players?.[slot.playerIndex] || null;
  }, []);

  const swapManualRoundPlayers = useCallback((games, bench, firstSlot, secondSlot) => {
    const nextGames = cloneGames(games);
    const nextBench = [...bench];
    const firstPlayer = getPlayerFromManualSlot(nextGames, nextBench, firstSlot);
    const secondPlayer = getPlayerFromManualSlot(nextGames, nextBench, secondSlot);

    if (!firstPlayer || !secondPlayer) {
      return { games: nextGames, bench: nextBench };
    }

    const writePlayer = (slot, value) => {
      if (slot.type === 'bench') {
        nextBench[slot.index] = value;
        return;
      }

      nextGames[slot.courtIndex][slot.team].players[slot.playerIndex] = value;
    };

    writePlayer(firstSlot, secondPlayer);
    writePlayer(secondSlot, firstPlayer);

    return { games: nextGames, bench: nextBench };
  }, [cloneGames, getPlayerFromManualSlot]);

  const resetManualRoundDraft = useCallback(() => {
    setManualRoundGames(cloneGames(currentGames));
    setManualRoundBench([...benchPlayers]);
    setSelectedManualSlot(null);
    setManualRoundError('');
  }, [benchPlayers, cloneGames, currentGames]);

  const handleOpenArrangeFirstRound = useCallback(() => {
    resetManualRoundDraft();
    setShowArrangeFirstRound(true);
  }, [resetManualRoundDraft]);

  const handleCloseArrangeFirstRound = useCallback(() => {
    setShowArrangeFirstRound(false);
    setSelectedManualSlot(null);
    setManualRoundError('');
  }, []);

  const handleManualRoundSlotClick = useCallback((slot) => {
    setManualRoundError('');

    if (!selectedManualSlot) {
      setSelectedManualSlot(slot);
      return;
    }

    if (getManualSlotKey(selectedManualSlot) === getManualSlotKey(slot)) {
      setSelectedManualSlot(null);
      return;
    }

    const nextLayout = swapManualRoundPlayers(manualRoundGames, manualRoundBench, selectedManualSlot, slot);
    setManualRoundGames(nextLayout.games);
    setManualRoundBench(nextLayout.bench);
    setSelectedManualSlot(null);
  }, [getManualSlotKey, manualRoundBench, manualRoundGames, selectedManualSlot, swapManualRoundPlayers]);

  const validateManualRound = useCallback((games, bench) => {
    const activePlayers = tournamentData.players.filter((player) => playerStatus[player] !== 'inactive');
    const assignedPlayers = [
      ...bench,
      ...games.flatMap((game) => [
        ...(game?.team1?.players || []),
        ...(game?.team2?.players || []),
      ]),
    ];

    const uniquePlayers = new Set(assignedPlayers);
    const activePlayerSet = new Set(activePlayers);

    if (
      assignedPlayers.length !== activePlayers.length
      || uniquePlayers.size !== activePlayers.length
      || assignedPlayers.some((player) => !activePlayerSet.has(player))
      || activePlayers.some((player) => !uniquePlayers.has(player))
    ) {
      return translate('tournament.manualFirstRoundInvalid');
    }

    if (games.some((game) => game?.team1?.players?.length !== 2 || game?.team2?.players?.length !== 2)) {
      return translate('tournament.manualFirstRoundInvalid');
    }

    if (tournamentData.mixedPairs) {
      const hasInvalidMixedPair = games.some((game) => ['team1', 'team2'].some((teamKey) => {
        const [firstPlayer, secondPlayer] = game?.[teamKey]?.players || [];
        if (!firstPlayer || !secondPlayer) return true;
        return tournamentData.playerGenders[firstPlayer] === tournamentData.playerGenders[secondPlayer];
      }));

      if (hasInvalidMixedPair) {
        return translate('tournament.manualFirstRoundMixedPairs');
      }
    }

    return '';
  }, [playerStatus, tournamentData, translate]);

  const handleSaveManualRound = useCallback(() => {
    const validationMessage = validateManualRound(manualRoundGames, manualRoundBench);
    if (validationMessage) {
      setManualRoundError(validationMessage);
      return;
    }

    const manualGamesSnapshot = cloneGames(manualRoundGames);
    const manualBenchSnapshot = [...manualRoundBench];
    const manualPartnerships = extractPartnershipsFromGames(manualGamesSnapshot);

    setCurrentGames(manualGamesSnapshot);
    setBenchPlayers(manualBenchSnapshot);
    setUsedPartnerships(manualPartnerships);
    setLastRoundPartnerships(manualPartnerships);
    setPartnershipPlayCount(buildPartnershipPlayCountMap(manualPartnerships));
    setBenchCount(buildBenchCountMap(tournamentData.players, manualBenchSnapshot));
    setShowArrangeFirstRound(false);
    setSelectedManualSlot(null);
    setManualRoundError('');
    setManualRoundSaved(true);
  }, [cloneGames, manualRoundBench, manualRoundGames, tournamentData, validateManualRound]);

  const handleDraftScoreChange = (courtIndex, team, value, baseGames) => {
    const roundType = tournamentData?.roundType || 'points';
    const maxPoints = tournamentData?.targetPoints || 21;

    setPreviousRoundDraft((prev) => {
      const base = prev || cloneGames(baseGames || []);
      const newGames = cloneGames(base);
      const game = newGames[courtIndex];
      if (!game) return base;
      const parsedScore = parseInt(value, 10);
      const score = Number.isNaN(parsedScore)
        ? null
        : Math.min(Math.max(parsedScore, 0), roundType === 'points' ? maxPoints : parsedScore);

      if (score === null) {
        if (roundType === 'points') {
          game.team1.score = null;
          game.team2.score = null;
        } else if (team === 1) {
          game.team1.score = null;
        } else {
          game.team2.score = null;
        }

        return newGames;
      }

      if (roundType === 'points') {
        if (team === 1) {
          game.team1.score = score;
          game.team2.score = Math.max(0, maxPoints - score);
        } else {
          game.team2.score = score;
          game.team1.score = Math.max(0, maxPoints - score);
        }
      } else {
        if (team === 1) {
          game.team1.score = score;
        } else {
          game.team2.score = score;
        }
      }

      return newGames;
    });
  };

  const applyResultDelta = useCallback((result, team1Players, team2Players, deltaWins, deltaLosses, deltaDraws, direction) => {
    if (result === 1) {
      team1Players.forEach((p) => { deltaWins[p] = (deltaWins[p] || 0) + direction; });
      team2Players.forEach((p) => { deltaLosses[p] = (deltaLosses[p] || 0) + direction; });
    } else if (result === 2) {
      team2Players.forEach((p) => { deltaWins[p] = (deltaWins[p] || 0) + direction; });
      team1Players.forEach((p) => { deltaLosses[p] = (deltaLosses[p] || 0) + direction; });
    } else {
      [...team1Players, ...team2Players].forEach((p) => { deltaDraws[p] = (deltaDraws[p] || 0) + direction; });
    }
  }, []);

  const getResultCode = useCallback((game) => {
    if (!game) return 0;
    const a = Number(game.team1.score);
    const b = Number(game.team2.score);
    if (a > b) return 1;
    if (b > a) return 2;
    return 0;
  }, []);

  const savePreviousRoundEdits = useCallback(() => {
    if (!tournamentData || tournamentData.isComplete) return;
    if (tournamentData.format !== 'americano') return;
    if (viewRoundNumber !== roundNumber - 1) return;

    const entry = roundHistory.find((r) => r.round === viewRoundNumber);
    if (!entry || !previousRoundDraft) return;

    const originalGames = entry.games || [];
    const editedGames = previousRoundDraft || [];

    if (!editedGames.every((game) => game?.team1?.score !== null && game?.team2?.score !== null)) {
      alert(translate('tournament.enterScores'));
      return;
    }

    const nextScores = { ...playerScores };
    const nextWins = { ...wins };
    const nextLosses = { ...losses };
    const nextDraws = { ...draws };
    const nextLastGameScores = { ...lastGameScores };

    for (let i = 0; i < originalGames.length; i++) {
      const oldGame = originalGames[i];
      const newGame = editedGames[i];
      if (!oldGame || !newGame) continue;

      const oldTeam1 = oldGame.team1.players;
      const oldTeam2 = oldGame.team2.players;
      const oldScore1 = Number(oldGame.team1.score) || 0;
      const oldScore2 = Number(oldGame.team2.score) || 0;

      const newTeam1 = newGame.team1.players;
      const newTeam2 = newGame.team2.players;
      const newScore1 = Number(newGame.team1.score) || 0;
      const newScore2 = Number(newGame.team2.score) || 0;

      // Apply score deltas
      oldTeam1.forEach((p) => {
        nextScores[p] = (nextScores[p] || 0) + (newScore1 - oldScore1);
        nextLastGameScores[p] = newScore1;
      });
      oldTeam2.forEach((p) => {
        nextScores[p] = (nextScores[p] || 0) + (newScore2 - oldScore2);
        nextLastGameScores[p] = newScore2;
      });

      // Apply W/L/D deltas
      const oldResult = getResultCode(oldGame);
      const newResult = getResultCode(newGame);
      if (oldResult !== newResult) {
        applyResultDelta(oldResult, oldTeam1, oldTeam2, nextWins, nextLosses, nextDraws, -1);
        applyResultDelta(newResult, newTeam1, newTeam2, nextWins, nextLosses, nextDraws, +1);
      }
    }

    setPlayerScores(nextScores);
    setWins(nextWins);
    setLosses(nextLosses);
    setDraws(nextDraws);
    setLastGameScores(nextLastGameScores);

    const newGamesSnapshot = cloneGames(editedGames);
    setRoundHistory((prev) => prev.map((r) => (r.round === viewRoundNumber ? { ...r, games: newGamesSnapshot } : r)));
    previousRoundOriginalJsonRef.current = JSON.stringify(newGamesSnapshot);
    setPreviousRoundSavedRound(viewRoundNumber);
  }, [applyResultDelta, cloneGames, draws, getResultCode, lastGameScores, losses, playerScores, previousRoundDraft, roundHistory, roundNumber, tournamentData, translate, viewRoundNumber, wins]);

  const cancelPreviousRoundEdits = useCallback(() => {
    const entry = roundHistory.find((r) => r.round === viewRoundNumber);
    if (!entry) return;
    const original = cloneGames(entry.games || []);
    setPreviousRoundDraft(original);
    previousRoundOriginalJsonRef.current = JSON.stringify(entry.games || []);
  }, [cloneGames, roundHistory, viewRoundNumber]);

  // Persist current in-progress tournament state to localStorage and Firebase
  useEffect(() => {
    if (finishingRef.current || tournamentData?.name !== name) return;
    const encoded = buildEncodedState();
    if (!encoded || !name) return;
    
    // Save to localStorage
    saveToLocalStorage(name, {
      name,
      firebaseId,
      players: tournamentData?.players,
      playerStatus,
      courts: tournamentData?.courts,
      format: tournamentData?.format,
      mexicanoPairing: tournamentData?.mexicanoPairing,
      mixedPairs: tournamentData?.mixedPairs,
      playerGenders: tournamentData?.playerGenders,
      roundType: tournamentData?.roundType,
      targetPoints: tournamentData?.targetPoints,
      roundMinutes: tournamentData?.roundMinutes,
      games: currentGames,
      scores: playerScores,
      isComplete: tournamentData?.isComplete || false,
      playCount,
      playCountOffset,
      wins,
      losses,
      draws,
      roundHistory,
      round: roundNumber,
      allPartnerships,
      usedPartnerships,
      partnershipPlayCount,
      lastRoundPartnerships,
      benchCount,
      timeLeft,
      roundEnded,
      lastGameScores
    });

    // Save to Firebase if we have a Firebase ID
    if (firebaseId) {
      saveTournamentToFirebase(firebaseId, {
        name,
        firebaseId,
        players: tournamentData?.players,
        playerStatus,
        courts: tournamentData?.courts,
        format: tournamentData?.format,
        mexicanoPairing: tournamentData?.mexicanoPairing,
        mixedPairs: tournamentData?.mixedPairs,
        playerGenders: tournamentData?.playerGenders,
        roundType: tournamentData?.roundType,
        targetPoints: tournamentData?.targetPoints,
        roundMinutes: tournamentData?.roundMinutes,
        games: currentGames,
        scores: playerScores,
        isComplete: tournamentData?.isComplete || false,
        playCount,
        playCountOffset,
        wins,
        losses,
        draws,
        roundHistory,
        round: roundNumber,
        allPartnerships,
        usedPartnerships,
        partnershipPlayCount,
        lastRoundPartnerships,
        benchCount,
        timeLeft,
        roundEnded,
        lastGameScores
      });
    }

    // Broadcast live state so any open TV mode tabs stay in sync
    postTvState(name, encoded, tvChannelRef);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allPartnerships, benchCount, buildEncodedState, currentGames, draws, firebaseId, lastGameScores, lastRoundPartnerships, losses, name, partnershipPlayCount, playCount, playCountOffset, playerScores, roundEnded, roundHistory, roundNumber, timeLeft, tournamentData, usedPartnerships, wins]);

  // Handle share functionality - shares Firebase TV link when available, falls back to local TV route
  const handleShare = useCallback(() => {
    if (firebaseId && database) {
      // Firebase available: share real-time cross-device link
      const shareUrl = `${window.location.origin}/tv/${firebaseId}`;
      navigator.clipboard.writeText(shareUrl);
      setShowCopied(true);
    } else {
      // No Firebase: share the local BroadcastChannel TV URL (same device)
      const localTvUrl = `${window.location.origin}/tournament/${encodeURIComponent(name)}/tv`;
      navigator.clipboard.writeText(localTvUrl);
      setShowCopied(true);
    }
  }, [firebaseId, name]);

  // Handle time-based rounds (automatic whistle and visual signal)
  useEffect(() => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }

    // Hydration has not restored the round yet: leave its persisted start time intact.
    if (!tournamentData || tournamentData.name !== name || finishingRef.current) return undefined;

    if (tournamentData.roundType !== 'time' || !currentGames?.length) {
      setTimeLeft(null);
      setRoundEnded(false);
      try {
        localStorage.removeItem(timerStorageKey);
      } catch (error) {
        console.warn('Unable to clear timer storage', error);
      }
      return undefined;
    }

    const roundSeconds = (tournamentData.roundMinutes || 12) * 60;
    let stored = null;
    try {
      const raw = localStorage.getItem(timerStorageKey);
      stored = raw ? JSON.parse(raw) : null;
    } catch (error) {
      console.warn('Unable to read timer storage', error);
    }

    const isSameRound = stored && stored.round === roundNumber;
    const startedAt = isSameRound && stored.startedAt ? stored.startedAt : Date.now();
    const elapsedAtMount = Math.floor((Date.now() - startedAt) / 1000);
    const remainingAtMount = roundSeconds - elapsedAtMount;

    if (isSameRound && stored?.ended) {
      setTimeLeft(0);
      setRoundEnded(true);
      return undefined;
    }

    if (remainingAtMount <= 0) {
      setTimeLeft(0);
      setRoundEnded(true);
      try {
        localStorage.setItem(
          timerStorageKey,
          JSON.stringify({ round: roundNumber, startedAt, duration: roundSeconds, ended: true })
        );
      } catch (error) {
        console.warn('Unable to persist timer storage', error);
      }
      return undefined;
    }

    setRoundEnded(false);
    setTimeLeft(remainingAtMount);
    try {
      localStorage.setItem(
        timerStorageKey,
        JSON.stringify({ round: roundNumber, startedAt, duration: roundSeconds, ended: false })
      );
    } catch (error) {
      console.warn('Unable to persist timer storage', error);
    }

    timerIntervalRef.current = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startedAt) / 1000);
      const remaining = roundSeconds - elapsed;

      if (remaining <= 0) {
        setTimeLeft(0);
        setRoundEnded(true);
        try {
          localStorage.setItem(
            timerStorageKey,
            JSON.stringify({ round: roundNumber, startedAt, duration: roundSeconds, ended: true })
          );
        } catch (error) {
          console.warn('Unable to persist timer storage', error);
        }
        playWhistle();
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      } else {
        setTimeLeft(remaining);
      }
    }, 1000);

    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    };
  }, [
    currentGames?.length,
    name,
    playWhistle,
    roundNumber,
    timerStorageKey,
    tournamentData,
    tournamentData?.roundMinutes,
    tournamentData?.roundType
  ]);

  const handleOpenTvMode = () => {
    let tvUrl;
    if (firebaseId && database) {
      // Firebase available: open cross-device real-time TV
      tvUrl = `${window.location.origin}/tv/${firebaseId}`;
    } else {
      // No Firebase: open local BroadcastChannel TV (same device, different tab)
      tvUrl = `${window.location.origin}/tournament/${encodeURIComponent(name)}/tv`;
    }
    window.open(tvUrl, '_blank', 'noopener,noreferrer');
  };

  const handleEndTournamentConfirmed = () => {
    if (finishingRef.current) return;
    finishingRef.current = true;
    clearInterval(timerIntervalRef.current);
    timerIntervalRef.current = null;

    // Compute final scores/play counts including any uncommitted (in-progress) round
    const finalScores = { ...playerScores };
    const finalPlayCount = { ...playCount };
    const finalWins = { ...wins };
    const finalLosses = { ...losses };
    const finalDraws = { ...draws };

    currentGames.forEach(game => {
      if (game.team1.score != null && game.team2.score != null) {
        game.team1.players.forEach(p => {
          finalScores[p] = (finalScores[p] || 0) + game.team1.score;
          finalPlayCount[p] = (finalPlayCount[p] || 0) + 1;
        });
        game.team2.players.forEach(p => {
          finalScores[p] = (finalScores[p] || 0) + game.team2.score;
          finalPlayCount[p] = (finalPlayCount[p] || 0) + 1;
        });

        // Apply W/L/D for any uncommitted games
        if (game.team1.score > game.team2.score) {
          game.team1.players.forEach((p) => {
            finalWins[p] = (finalWins[p] || 0) + 1;
          });
          game.team2.players.forEach((p) => {
            finalLosses[p] = (finalLosses[p] || 0) + 1;
          });
        } else if (game.team2.score > game.team1.score) {
          game.team2.players.forEach((p) => {
            finalWins[p] = (finalWins[p] || 0) + 1;
          });
          game.team1.players.forEach((p) => {
            finalLosses[p] = (finalLosses[p] || 0) + 1;
          });
        } else {
          [...game.team1.players, ...game.team2.players].forEach((p) => {
            finalDraws[p] = (finalDraws[p] || 0) + 1;
          });
        }
      }
    });

    const playerData = {};
    tournamentData.players.forEach(p => {
      playerData[p] = {
        score: finalScores[p] || 0,
        playCount: finalPlayCount[p] || 0,
        wins: finalWins[p] || 0,
        losses: finalLosses[p] || 0,
        draws: finalDraws[p] || 0
      };
    });

    const finalState = {
      name,
      firebaseId,
      playerStatus,
      playerGenders: tournamentData.playerGenders,
      mixedPairs: tournamentData.mixedPairs,
      playCountOffset,
      partnershipPlayCount,
      lastGameScores,
      players: tournamentData.players,
      courts: tournamentData.courts,
      format: tournamentData.format,
      mexicanoPairing: tournamentData.mexicanoPairing,
      roundType: tournamentData.roundType,
      targetPoints: tournamentData.targetPoints,
      roundMinutes: tournamentData.roundMinutes,
      scores: finalScores,
      playCount: finalPlayCount,
      wins: finalWins,
      losses: finalLosses,
      draws: finalDraws,
      roundHistory,
      playerData,
      isComplete: true,
      round: roundNumber,
      allPartnerships,
      usedPartnerships,
      lastRoundPartnerships,
      benchCount,
      // Save the last round state
      lastGames: currentGames,
      lastBenchPlayers: benchPlayers,
      games: [] // Clear active games when tournament ends
    };

    // Results are available offline; remote acknowledgment must not block completion.
    // The finishing guard prevents timer/autosave writes from following this final snapshot.
    saveToLocalStorage(name, finalState);
    if (firebaseId) saveTournamentToFirebase(firebaseId, finalState);

    const encodedState = encodeFinalTvState(finalState);
    const query = new URLSearchParams({ state: encodedState });
    navigate(`/results/${encodeURIComponent(name)}?${query}`);
    postTvState(name, encodedState, tvChannelRef);
  };

  const handleEndTournamentClick = () => {
    setShowEndConfirmation(true);
  };

  const renderScoreSelect = (games, courtIndex, team, onScoreChange, disabled) => {
    const game = games[courtIndex];
    const score = team === 1 ? game.team1.score : game.team2.score;
    const roundType = tournamentData?.roundType || 'points';
    const maxScore = roundType === 'points' ? (tournamentData?.targetPoints || 21) : 30;

    return (
      <FormControl size="medium" sx={{ width: '100%', maxWidth: 140, mt: 1 }}>
        <TextField
          type="number"
          label={translate('common.score')}
          value={score ?? ''}
          onChange={(e) => {
            const value = e.target.value;
            if (value === '') {
              onScoreChange(courtIndex, team, '');
              return;
            }

            const numericValue = Number(value);
            const clampedValue = Number.isNaN(numericValue)
              ? ''
              : Math.min(maxScore, Math.max(0, numericValue));

            onScoreChange(courtIndex, team, clampedValue);
          }}
          onKeyDown={(e) => {
            // Prevent e, E, +, -, . which are allowed in number inputs but not valid for scores
            if (['e', 'E', '+', '-', '.'].includes(e.key)) {
              e.preventDefault();
            }
          }}
          disabled={disabled}
          inputProps={{
            min: 0,
            max: maxScore,
            step: 1,
            inputMode: 'numeric',
            pattern: '[0-9]*'
          }}
        />
      </FormControl>
    );
  };

  const renderBench = (benchList) => (
    <Paper sx={{ p: { xs: 3, sm: 4 }, mb: 4, backgroundColor: 'secondary.dark', borderRadius: 2 }}>
      <Typography variant="h6" gutterBottom sx={{ mb: 3, color: 'text.primary' }}>
        {translate('tournament.bench')}
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)' }, gap: 3 }}>
        {benchList.map((player, index) => (
          <Paper key={index} sx={{ textAlign: 'center', bgcolor: 'background.paper', p: 2 }}>
            <Typography variant="body2" sx={{ color: 'text.primary' }}>
              <Box component="span" sx={{ fontWeight: 'bold', fontSize: '1.1em', color: 'text.primary' }}>{player}</Box>
              <Box component="span" sx={{ display: 'block', fontWeight: 'normal', fontSize: '1em', fontStyle: "italic", color: 'text.secondary' }}>
                {translate('tournament.playedMatches')}: {playCount[player] || 0}
              </Box>
            </Typography>
          </Paper>
        ))}
      </Box>
    </Paper>
  );
  
  const renderCourt = (games, game, index, onScoreChange, disabled, headerRound) => (
    <Paper key={`${headerRound}-${index}-${game.courtId ?? 'court'}`} sx={{ p: { xs: 3, sm: 4 }, mb: 3, borderRadius: 2, boxShadow: 2 }}>
      <Typography variant="h6" gutterBottom sx={{ 
        textAlign: 'center', 
        borderBottom: 1, 
        borderColor: 'divider', 
        pb: 2, 
        mb: 3 
      }}>
        {translate('common.court')} {game.courtId} ({translate('tournament.round')} {headerRound})
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box sx={{ width: '50%' }}>
          <Typography variant="subtitle2" gutterBottom>
            {translate('common.team')} 1
          </Typography>
          {game.team1.players.map(player => (
            <Typography key={player}>
              <Box component="span" sx={{ fontWeight: 'bold', fontSize: '1.1em' }}>{player}</Box>
              <Box component="span" sx={{ fontWeight: 'normal', fontSize: '0.85em', fontStyle: 'italic'  }}> 
              ({translate('tournament.playedMatches')}: {playCount[player] || 0})
              </Box>
            </Typography>
          ))}
          {renderScoreSelect(games, index, 1, onScoreChange, disabled)}
        </Box>

        <Typography variant="h5" sx={{ color: 'text.secondary', px: 2, display: { xs: 'none', sm: 'block' } }}>
          VS
        </Typography>
        
        <Box sx={{ width: '50%', textAlign: 'right' }}>
          <Typography variant="subtitle2" gutterBottom>
            {translate('common.team')} 2
          </Typography>
          {game.team2.players.map(player => (
            <Typography key={player}>
              <Box component="span" sx={{ fontWeight: 'bold', fontSize: '1.1em' }}>{player}</Box>
              <Box component="span" sx={{ fontWeight: 'normal', fontSize: '0.85em', fontStyle: 'italic' }}> 
                ({translate('tournament.playedMatches')}: {playCount[player] || 0})
              </Box>
            </Typography>
          ))}
          {renderScoreSelect(games, index, 2, onScoreChange, disabled)}
        </Box>
      </Box>
    </Paper>
  );

  const renderManualPlayerCard = (player, slot, options = {}) => {
    const {
      caption,
      accentColor = 'divider',
      backgroundColor = 'background.paper',
    } = options;
    const isSelected = selectedManualSlot && getManualSlotKey(selectedManualSlot) === getManualSlotKey(slot);
    const genderLabel = tournamentData?.mixedPairs
      ? (tournamentData.playerGenders[player] === 'female'
          ? translate('playerSetup.female')
          : translate('playerSetup.male'))
      : null;

    return (
      <Paper
        key={`${getManualSlotKey(slot)}-${player}`}
        component="button"
        type="button"
        onClick={() => handleManualRoundSlotClick(slot)}
        elevation={0}
        sx={{
          width: '100%',
          p: 1.5,
          textAlign: 'left',
          borderRadius: 2,
          border: '2px solid',
          borderColor: isSelected ? 'primary.main' : accentColor,
          backgroundColor: isSelected ? 'action.selected' : backgroundColor,
          cursor: 'pointer',
          transition: 'border-color 0.2s ease, transform 0.2s ease, background-color 0.2s ease',
          '&:hover': {
            borderColor: 'primary.main',
            transform: 'translateY(-1px)',
          },
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          {player}
        </Typography>
        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.5 }}>
          {caption}
          {genderLabel ? ` • ${genderLabel}` : ''}
        </Typography>
      </Paper>
    );
  };




  const maxViewRound = roundNumber;
  const isViewingCurrentRound = viewRoundNumber === roundNumber;
  const hasStartedCurrentRound = currentGames.some((game) => game.team1.score !== null || game.team2.score !== null);
  const viewedRoundEntry = isViewingCurrentRound
    ? null
    : roundHistory.find((r) => r.round === viewRoundNumber);

  const canArrangeFirstRound = isViewingCurrentRound
    && roundNumber === 1
    && !hasStartedCurrentRound;

  const canEditViewedRound = !tournamentData?.isComplete
    && tournamentData?.format === 'americano'
    && viewRoundNumber === roundNumber - 1
    && !!viewedRoundEntry;

  useEffect(() => {
    if (canEditViewedRound) {
      const originalGames = viewedRoundEntry?.games || [];
      const originalJson = JSON.stringify(originalGames);
      if (!previousRoundDraft || previousRoundOriginalJsonRef.current !== originalJson) {
        setPreviousRoundDraft(cloneGames(originalGames));
        previousRoundOriginalJsonRef.current = originalJson;
      }
    } else {
      setPreviousRoundDraft(null);
      previousRoundOriginalJsonRef.current = null;
    }
  }, [canEditViewedRound, cloneGames, previousRoundDraft, viewedRoundEntry]);

  // Edit Roster handlers
  const handleOpenEditRoster = useCallback(() => {
    if (!tournamentData) return;
    // Initialize edit roster state with current data
    const editPlayers = tournamentData.players.map((name) => ({
      name,
      gender: tournamentData.playerGenders?.[name] || 'male',
      status: playerStatus[name] || 'active'
    }));
    setEditRosterPlayers(editPlayers);
    setEditRosterCourts(tournamentData.courts);
    setEditRosterNewPlayer('');
    setEditRosterNewGender('male');
    setEditRosterError('');
    setShowEditRoster(true);
  }, [tournamentData, playerStatus]);

  const handleCloseEditRoster = useCallback(() => {
    setShowEditRoster(false);
    setEditRosterError('');
  }, []);

  const handleEditRosterRemovePlayer = useCallback((playerName) => {
    // Show confirmation dialog
    setRemovePlayerConfirm({ open: true, playerName });
  }, []);

  const handleRemovePlayerMakeInactive = useCallback(() => {
    const playerName = removePlayerConfirm.playerName;
    if (playerName) {
      setEditRosterPlayers((prev) => 
        prev.map((p) => p.name === playerName ? { ...p, status: 'inactive' } : p)
      );
    }
    setRemovePlayerConfirm({ open: false, playerName: null });
  }, [removePlayerConfirm.playerName]);

  const handleRemovePlayerCompletely = useCallback(() => {
    // Show final confirmation dialog
    const playerName = removePlayerConfirm.playerName;
    setRemovePlayerConfirm({ open: false, playerName: null });
    setRemovePlayerFinalConfirm({ open: true, playerName });
  }, [removePlayerConfirm.playerName]);

  const handleRemovePlayerFinalConfirm = useCallback(() => {
    const playerName = removePlayerFinalConfirm.playerName;
    if (playerName) {
      // Remove player completely from the list
      setEditRosterPlayers((prev) => 
        prev.filter((p) => p.name !== playerName)
      );
    }
    setRemovePlayerFinalConfirm({ open: false, playerName: null });
  }, [removePlayerFinalConfirm.playerName]);

  const handleRemovePlayerFinalCancel = useCallback(() => {
    setRemovePlayerFinalConfirm({ open: false, playerName: null });
  }, []);

  const handleRemovePlayerCancel = useCallback(() => {
    setRemovePlayerConfirm({ open: false, playerName: null });
  }, []);

  const handleEditRosterReactivatePlayer = useCallback((playerName) => {
    setEditRosterPlayers((prev) =>
      prev.map((p) => p.name === playerName ? { ...p, status: 'active' } : p)
    );
  }, []);

  const handleEditRosterAddPlayer = useCallback(() => {
    const trimmedName = editRosterNewPlayer.trim();
    if (!trimmedName) {
      setEditRosterError(translate('playerSetup.playerRequired'));
      return;
    }
    // Check for duplicate names (case-insensitive) including inactive players
    const normalizedName = trimmedName.toLowerCase();
    const isDuplicate = editRosterPlayers.some(
      (p) => p.name.toLowerCase() === normalizedName
    );
    if (isDuplicate) {
      setEditRosterError(translate('playerSetup.duplicateName'));
      return;
    }
    setEditRosterPlayers((prev) => [
      ...prev,
      { name: trimmedName, gender: editRosterNewGender, status: 'active', isNew: true }
    ]);
    setEditRosterNewPlayer('');
    setEditRosterError('');
  }, [editRosterNewPlayer, editRosterNewGender, editRosterPlayers, translate]);

  const handleEditRosterApply = useCallback(() => {
    const activePlayers = editRosterPlayers.filter((p) => p.status === 'active');
    
    // Validate minimum players
    if (activePlayers.length < 4) {
      setEditRosterError(translate('tournament.minPlayersWarning'));
      return;
    }

    // Auto-adjust courts if needed
    const maxCourts = Math.floor(activePlayers.length / 4);
    const finalCourts = Math.min(editRosterCourts, maxCourts);

    // Validate mixed pairs gender balance if enabled
    if (tournamentData.mixedPairs) {
      const maleCount = activePlayers.filter((p) => p.gender === 'male').length;
      const femaleCount = activePlayers.filter((p) => p.gender === 'female').length;
      if (maleCount !== femaleCount) {
        setEditRosterError(translate('tournament.mixedPairsWarning'));
        return;
      }
    }

    // Build new player list (all players including inactive)
    const allPlayerNames = editRosterPlayers.map((p) => p.name);
    const newPlayerGenders = {};
    const newPlayerStatus = {};
    editRosterPlayers.forEach((p) => {
      newPlayerGenders[p.name] = p.gender;
      newPlayerStatus[p.name] = p.status;
    });

    const rolledBackTracking = rollbackRoundReservations({
      players: tournamentData.players,
      games: currentGames,
      benchPlayers,
      benchCount,
      usedPartnerships,
      partnershipPlayCount,
    });

    // Initialize stats for new players
    const newPlayers = editRosterPlayers.filter((p) => p.isNew);
    const updatedScores = { ...playerScores };
    const updatedPlayCount = { ...playCount };
    const updatedWins = { ...wins };
    const updatedLosses = { ...losses };
    const updatedDraws = { ...draws };
    const updatedBenchCount = { ...rolledBackTracking.benchCount };
    const updatedPlayCountOffset = { ...playCountOffset };

    // Give every newcomer the same lowest positive adjusted count from the
    // existing active roster. Zero is only the baseline before anyone has played.
    const currentActivePlayers = editRosterPlayers.filter((p) => !p.isNew && p.status === 'active');
    let minActivePlayCount = 0;
    if (currentActivePlayers.length > 0 && newPlayers.length > 0) {
      const activePlayCounts = currentActivePlayers
        .map((p) => (playCount[p.name] || 0) + (playCountOffset[p.name] || 0))
        .filter((count) => count > 0);
      if (activePlayCounts.length > 0) {
        minActivePlayCount = Math.min(...activePlayCounts);
      }
    }

    newPlayers.forEach((p) => {
      updatedScores[p.name] = 0;
      updatedPlayCount[p.name] = 0;
      updatedWins[p.name] = 0;
      updatedLosses[p.name] = 0;
      updatedDraws[p.name] = 0;
      updatedBenchCount[p.name] = 0;
      // Set playCountOffset so new players don't dominate rotation
      updatedPlayCountOffset[p.name] = minActivePlayCount;
    });

    // Update tournament data
    setTournamentData((prev) => ({
      ...prev,
      players: allPlayerNames,
      playerGenders: newPlayerGenders,
      courts: finalCourts
    }));

    // Update all tracking states
    setPlayerStatus(newPlayerStatus);
    setPlayerScores(updatedScores);
    setPlayCount(updatedPlayCount);
    setPlayCountOffset(updatedPlayCountOffset);
    setWins(updatedWins);
    setLosses(updatedLosses);
    setDraws(updatedDraws);
    setBenchCount(updatedBenchCount);

    // Regenerate partnerships for active players only
    const activePlayerNames = activePlayers.map((p) => p.name);
    const activePlayerSet = new Set(activePlayerNames);
    const newPartnerships = generateAllPartnerships(
      activePlayerNames,
      newPlayerGenders,
      tournamentData.mixedPairs
    );
    setAllPartnerships(newPartnerships);

    // Filter usedPartnerships to only include partnerships where BOTH players are still active
    // This preserves pairing history for existing player pairs while cleaning up removed players
    const filteredUsedPartnerships = rolledBackTracking.usedPartnerships.filter(
      p => activePlayerSet.has(p[0]) && activePlayerSet.has(p[1])
    );
    setUsedPartnerships(filteredUsedPartnerships);

    const activePartnershipKeys = new Set(newPartnerships.map((pair) => partnershipKey(pair)));
    const filteredPartnershipPlayCount = {};
    Object.entries(rolledBackTracking.partnershipPlayCount).forEach(([key, count]) => {
      if (activePartnershipKeys.has(key)) {
        filteredPartnershipPlayCount[key] = count;
      }
    });
    setPartnershipPlayCount(filteredPartnershipPlayCount);

    const previousCompletedRound = roundHistory[roundHistory.length - 1];
    const previousRoundPartnerships = extractPartnershipsFromGames(previousCompletedRound?.games || [])
      .filter(([first, second]) => activePlayerSet.has(first) && activePlayerSet.has(second));
    const previousBenchPlayers = (previousCompletedRound?.benchPlayers || [])
      .filter((player) => activePlayerSet.has(player));

    // Close modal
    setShowEditRoster(false);

    // Reset timer for timed games when roster changes
    if (tournamentData.roundType === 'time') {
      const roundSeconds = (tournamentData.roundMinutes || 12) * 60;
      setTimeLeft(roundSeconds);
      setRoundEnded(false);
      try {
        localStorage.setItem(timerStorageKey, JSON.stringify({
          round: roundNumber,
          startedAt: Date.now(),
          ended: false
        }));
      } catch (error) {
        console.warn('Unable to save timer storage', error);
      }
    }

    // Regenerate current round with new roster (this will reset scores)
    // Use setTimeout to ensure state is updated before regenerating
    setTimeout(() => {
      // Clear current games to trigger regeneration
      setCurrentGames([]);
      setBenchPlayers([]);
      // Regenerate with updated state - pass all player data directly to avoid async state issues
      createNewRound({
        scores: updatedScores,
        playCount: updatedPlayCount,
        playCountOffset: updatedPlayCountOffset,
        playerStatus: newPlayerStatus,
        players: allPlayerNames,
        playerGenders: newPlayerGenders,
        courts: finalCourts,
        benchCount: updatedBenchCount,
        usedPartnerships: filteredUsedPartnerships,  // Pass filtered partnerships to preserve pairing history
        pairPlayCounts: filteredPartnershipPlayCount,
        lastRoundPartnerships: previousRoundPartnerships,
        previousBenchPlayers,
        skipRoundIncrement: true  // Don't increment round - we're regenerating the current round
      });
    }, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    editRosterPlayers,
    editRosterCourts,
    tournamentData,
    playerScores,
    playCount,
    playCountOffset,
    wins,
    losses,
    draws,
    benchCount,
    benchPlayers,
    currentGames,
    partnershipPlayCount,
    roundHistory,
    usedPartnerships,
    translate,
    createNewRound,
    timerStorageKey,
    roundNumber
  ]);

  // Calculate if courts need to be reduced for edit roster display
  const editRosterActivePlayers = editRosterPlayers.filter((p) => p.status === 'active');
  const editRosterMaxCourts = Math.floor(editRosterActivePlayers.length / 4);
  const editRosterCourtsWillReduce = editRosterCourts > editRosterMaxCourts && editRosterMaxCourts > 0;

  // Early return for loading state
  if (!tournamentData) {
    return <Typography>{translate('common.loading')}</Typography>;
  }

  const roundType = tournamentData.roundType || 'points';
  const targetPoints = tournamentData.targetPoints || 21;
  const roundMinutes = tournamentData.roundMinutes || 12;

  const benchToDisplay = isViewingCurrentRound
    ? benchPlayers
    : (viewedRoundEntry?.benchPlayers || []);

  const baseGamesToDisplay = isViewingCurrentRound
    ? currentGames
    : (viewedRoundEntry?.games || []);

  const gamesToDisplay = canEditViewedRound
    ? (previousRoundDraft || baseGamesToDisplay)
    : baseGamesToDisplay;

  const scoreChangeHandler = canEditViewedRound
    ? (courtIndex, team, value) => handleDraftScoreChange(courtIndex, team, value, baseGamesToDisplay)
    : handleScoreChange;

  const scoreInputsDisabled = !isViewingCurrentRound && !canEditViewedRound;

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60).toString().padStart(2, '0');
    const secs = Math.floor(seconds % 60).toString().padStart(2, '0');
    return `${mins}:${secs}`;
  };

  return (
    <Box>
      <TopActionBar>
        <IconButton onClick={handleShare} size="large" color="primary" aria-label={translate('common.share')}>
          <ShareIcon />
        </IconButton>
        <IconButton onClick={handleOpenTvMode} size="large" color="primary" aria-label={translate('tvMode.openButton')}>
          <TvIcon />
        </IconButton>
      </TopActionBar>

      {/* Prefer unseen partnerships is always enabled by default; toggle removed per request */}

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 2 }}>
        <Paper sx={{ p: 1.5, flex: 1 }}>
          <Typography
            variant="body2"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2
            }}
            noWrap
          >
            <Box component="span" sx={{ color: 'text.secondary', fontWeight: 600 }}>
              {translate('tournament.roundSettings')}
            </Box>
            <Box component="span" sx={{ fontWeight: 800, color: 'primary.main' }}>
              {roundType === 'points'
                ? translate('tournament.pointsModeLabel', { points: targetPoints })
                : translate('tournament.timeModeLabel', { minutes: roundMinutes })}
            </Box>
          </Typography>
        </Paper>
        {roundType === 'time' && (
          <Paper
            sx={{
              p: 2,
              flex: 1,
              bgcolor: roundEnded ? 'warning.light' : 'background.paper',
              borderColor: roundEnded ? 'warning.main' : 'divider',
              borderStyle: 'solid',
              borderWidth: 1
            }}
          >
            <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 0.5 }}>
              {translate('tournament.timerStatus')}
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontVariantNumeric: 'tabular-nums',
                fontWeight: 700,
                fontSize: { xs: '1.5rem', sm: '1.75rem' }
              }}
            >
              {timeLeft !== null ? formatTime(timeLeft) : translate('common.loading')}
            </Typography>
            {roundEnded && (
              <Typography variant="body2" sx={{ mt: 1, color: 'warning.dark', fontWeight: 600 }}>
                {translate('tournament.timeUp')}
              </Typography>
            )}
          </Paper>
        )}
      </Box>

      <Paper sx={{ p: 1.5, mb: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <IconButton
            onClick={() => setViewRoundNumber((prev) => Math.max(1, prev - 1))}
            disabled={viewRoundNumber <= 1}
            aria-label="Previous round"
          >
            <ChevronLeftIcon />
          </IconButton>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {translate('tournament.round')} {viewRoundNumber} / {maxViewRound}
          </Typography>
          <IconButton
            onClick={() => setViewRoundNumber((prev) => Math.min(maxViewRound, prev + 1))}
            disabled={viewRoundNumber >= maxViewRound}
            aria-label="Next round"
          >
            <ChevronRightIcon />
          </IconButton>
        </Stack>
        {canEditViewedRound && (
          <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
            {translate('common.editable')}: {translate('tournament.round')} {viewRoundNumber}
          </Typography>
        )}
      </Paper>

      <Typography
        variant="h4"
        gutterBottom
        sx={{
          fontSize: { xs: '1.5rem', sm: '2.125rem' },
          mb: 2,
          px: 1,
          py: 0.5,
          borderRadius: 1,
          backgroundColor: highlightRoundHeader ? 'action.selected' : 'transparent',
          transition: 'background-color 450ms ease',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          color: 'primary.main'
        }}
      >
        <Box component="span" sx={{ flex: '1 1 auto' }}>
          {name} - {translate('tournament.round')} {viewRoundNumber}
        </Box>
        <Tooltip
          title={tournamentData?.format === 'mexicano'
            ? translate('playerSetup.formatMexicano')
            : translate('playerSetup.formatAmericano')}
          arrow
        >
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
            <FormatIcon format={tournamentData?.format} size={44} />
          </Box>
        </Tooltip>
      </Typography>

      {isViewingCurrentRound && (
        <Stack spacing={1.5} sx={{ mb: 2 }}>
          {canArrangeFirstRound && (
            <Button
              variant="contained"
              fullWidth
              startIcon={<EditIcon />}
              sx={{ py: 1.5, fontSize: '1.1rem' }}
              onClick={handleOpenArrangeFirstRound}
            >
              {translate('tournament.arrangeFirstRound')}
            </Button>
          )}

          <Button
            variant="outlined"
            fullWidth
            startIcon={<EditIcon />}
            sx={{ py: 1.5, fontSize: '1.1rem' }}
            onClick={handleOpenEditRoster}
          >
            {translate('tournament.editRoster')}
          </Button>
        </Stack>
      )}

      {benchToDisplay.length > 0 && renderBench(benchToDisplay)}
      
      <Box sx={{ mt: 3 }}>
        {gamesToDisplay.map((game, index) => renderCourt(gamesToDisplay, game, index, scoreChangeHandler, scoreInputsDisabled, viewRoundNumber))}
      </Box>

      {isViewingCurrentRound && (
        <Stack spacing={2} sx={{ mt: 3 }}>
          <Button
            variant="contained"
            fullWidth
            color="success"
            sx={{ py: 1.5, fontSize: '1.1rem' }}
            onClick={handleCompleteGames}
            disabled={isCompletingRound}
          >
            {isCompletingRound
              ? translate('tournament.completingRound')
              : translate('tournament.completeRound')}
          </Button>
          
          <EndTournamentButton
            variant="contained"
            fullWidth
            color="secondary"
            sx={{ py: 1.5, fontSize: '1.1rem' }}
            onClick={handleEndTournamentClick}
          >
            {translate('tournament.endTournament')}
          </EndTournamentButton>
        </Stack>
      )}

      {canEditViewedRound && !isViewingCurrentRound && (
        <Stack spacing={2} sx={{ mt: 3 }}>
          <Button
            variant="contained"
            fullWidth
            color="primary"
            sx={{ py: 1.5, fontSize: '1.1rem' }}
            onClick={savePreviousRoundEdits}
          >
            {translate('common.save')}
          </Button>
          <Button
            variant="outlined"
            fullWidth
            sx={{ py: 1.5, fontSize: '1.1rem' }}
            onClick={cancelPreviousRoundEdits}
          >
            {translate('common.cancel')}
          </Button>
        </Stack>
      )}

      <Dialog
        open={showEndConfirmation}
        onClose={() => setShowEndConfirmation(false)}
      >
        <DialogTitle>{translate('tournament.confirmEnd')}</DialogTitle>
        <DialogActions>
          <Button onClick={() => setShowEndConfirmation(false)}>
            {translate('tournament.no')}
          </Button>
          <Button 
            onClick={handleEndTournamentConfirmed} 
            variant="contained" 
            color="warning"
          >
            {translate('tournament.yes')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Previous round save confirmation */}
      <Snackbar
        open={Boolean(nextRoundReadyNumber)}
        autoHideDuration={3000}
        onClose={() => setNextRoundReadyNumber(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setNextRoundReadyNumber(null)}>
          {translate('tournament.nextRoundReady', { round: nextRoundReadyNumber })}
        </Alert>
      </Snackbar>

      <Snackbar
        open={Boolean(previousRoundSavedRound)}
        autoHideDuration={3000}
        onClose={() => setPreviousRoundSavedRound(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setPreviousRoundSavedRound(null)}>
          {translate('tournament.roundSaved', { round: previousRoundSavedRound })}
        </Alert>
      </Snackbar>

      <Snackbar
        open={manualRoundSaved}
        autoHideDuration={3000}
        onClose={() => setManualRoundSaved(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setManualRoundSaved(false)}>
          {translate('tournament.firstRoundUpdated')}
        </Alert>
      </Snackbar>

      {/* Share success/error snackbars */}
      <Snackbar
        open={showCopied}
        autoHideDuration={3000}
        onClose={() => setShowCopied(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setShowCopied(false)}>
          {translate('common.linkCopied')}
        </Alert>
      </Snackbar>
      <Snackbar
        open={shareError}
        autoHideDuration={5000}
        onClose={() => setShareError(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setShareError(false)}>
          {translate('common.shareFailed')}
        </Alert>
      </Snackbar>

      <Dialog
        open={showArrangeFirstRound}
        onClose={handleCloseArrangeFirstRound}
        maxWidth="md"
        fullWidth
        fullScreen={isSmallScreen}
      >
        <DialogTitle>{translate('tournament.arrangeFirstRoundTitle')}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {translate('tournament.arrangeFirstRoundHelp')}
          </Typography>

          {manualRoundError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {manualRoundError}
            </Alert>
          )}

          {manualRoundBench.length > 0 && (
            <Paper sx={{ p: 2, mb: 2, borderRadius: 2, backgroundColor: 'secondary.dark' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
                {translate('tournament.bench')}
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))' }, gap: 1.25 }}>
                {manualRoundBench.map((player, index) => renderManualPlayerCard(
                  player,
                  { type: 'bench', index },
                  {
                    caption: translate('tournament.manualFirstRoundBenchSlot', { number: index + 1 }),
                    accentColor: 'secondary.main',
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.92)',
                  }
                ))}
              </Box>
            </Paper>
          )}

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 2 }}>
            {manualRoundGames.map((game, courtIndex) => (
              <Paper
                key={`manual-court-${game.courtId}-${courtIndex}`}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5, textAlign: 'center' }}>
                  {translate('common.court')} {game.courtId}
                </Typography>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1fr) auto minmax(0, 1fr)' },
                    gap: 1.5,
                    alignItems: 'stretch',
                  }}
                >
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      border: '2px solid',
                      borderColor: 'primary.main',
                      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(25, 118, 210, 0.14)' : 'rgba(25, 118, 210, 0.08)',
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        mb: 1,
                        px: 1,
                        py: 0.25,
                        borderRadius: 999,
                        backgroundColor: 'primary.main',
                        color: 'primary.contrastText',
                        fontWeight: 700,
                      }}
                    >
                      {translate('common.team')} 1
                    </Typography>
                    <Stack spacing={1}>
                      {game.team1.players.map((player, playerIndex) => renderManualPlayerCard(
                        player,
                        { type: 'court', courtIndex, team: 'team1', playerIndex },
                        {
                          caption: `${translate('common.team')} 1`,
                          accentColor: 'primary.main',
                          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(25, 118, 210, 0.1)' : 'rgba(25, 118, 210, 0.05)',
                        }
                      ))}
                    </Stack>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minHeight: { xs: 'auto', sm: '100%' },
                    }}
                  >
                    <Typography
                      variant="overline"
                      sx={{
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 999,
                        border: '1px solid',
                        borderColor: 'divider',
                        color: 'text.secondary',
                        fontWeight: 800,
                        letterSpacing: '0.12em',
                        backgroundColor: 'background.default',
                      }}
                    >
                      VS
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      border: '2px solid',
                      borderColor: 'success.main',
                      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(46, 125, 50, 0.14)' : 'rgba(46, 125, 50, 0.08)',
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        mb: 1,
                        px: 1,
                        py: 0.25,
                        borderRadius: 999,
                        backgroundColor: 'success.main',
                        color: 'success.contrastText',
                        fontWeight: 700,
                      }}
                    >
                      {translate('common.team')} 2
                    </Typography>
                    <Stack spacing={1}>
                      {game.team2.players.map((player, playerIndex) => renderManualPlayerCard(
                        player,
                        { type: 'court', courtIndex, team: 'team2', playerIndex },
                        {
                          caption: `${translate('common.team')} 2`,
                          accentColor: 'success.main',
                          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(46, 125, 50, 0.1)' : 'rgba(46, 125, 50, 0.05)',
                        }
                      ))}
                    </Stack>
                  </Box>
                </Box>
              </Paper>
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, flexWrap: 'wrap', gap: 1, justifyContent: 'space-between' }}>
          <Button onClick={resetManualRoundDraft}>
            {translate('tournament.resetFirstRound')}
          </Button>
          <Stack direction="row" spacing={1} sx={{ ml: 'auto' }}>
            <Button onClick={handleCloseArrangeFirstRound}>
              {translate('common.cancel')}
            </Button>
            <Button variant="contained" onClick={handleSaveManualRound}>
              {translate('common.save')}
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>

      {/* Edit Roster Dialog */}
      <Dialog
        open={showEditRoster}
        onClose={handleCloseEditRoster}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { maxHeight: '90vh' } }}
      >
        <DialogTitle>{translate('tournament.editRosterTitle')}</DialogTitle>
        <DialogContent dividers>
          {/* Warning about round regeneration */}
          <Alert severity="warning" sx={{ mb: 2 }}>
            <AlertTitle sx={{ fontWeight: 700 }}>
              {translate('tournament.rosterWarningTitle')}
            </AlertTitle>
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              <li>{translate('tournament.rosterWarningRegenerate')}</li>
              <li>{translate('tournament.rosterWarningScores')}</li>
              <li>{translate('tournament.rosterWarningNewPlayers')}</li>
              <li>{translate('tournament.rosterWarningSafe')}</li>
            </Box>
          </Alert>

          {/* Courts selector */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
              {translate('playerSetup.courtsTitle')}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <IconButton
                onClick={() => setEditRosterCourts((c) => Math.max(1, c - 1))}
                disabled={editRosterCourts <= 1}
                size="small"
              >
                <RemoveIcon />
              </IconButton>
              <Typography variant="h6" sx={{ minWidth: 40, textAlign: 'center' }}>
                {editRosterCourtsWillReduce ? editRosterMaxCourts : editRosterCourts}
              </Typography>
              <IconButton
                onClick={() => setEditRosterCourts((c) => c + 1)}
                disabled={editRosterCourts >= editRosterMaxCourts}
                size="small"
              >
                <AddIcon />
              </IconButton>
            </Stack>
            {editRosterCourtsWillReduce && (
              <Typography variant="caption" color="warning.main" sx={{ mt: 0.5, display: 'block' }}>
                {translate('tournament.courtsReduced', { courts: editRosterMaxCourts })}
              </Typography>
            )}
          </Box>

          {/* Active Players */}
          <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
            {translate('common.player')} ({editRosterActivePlayers.length})
          </Typography>
          <List dense sx={{ mb: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
            {editRosterPlayers
              .filter((p) => p.status === 'active')
              .map((player) => (
                <ListItem key={player.name} sx={{ py: 0.5 }}>
                  <ListItemText
                    primary={player.name}
                    secondary={tournamentData?.mixedPairs ? (player.gender === 'male' ? 'M' : 'F') : undefined}
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={() => handleEditRosterRemovePlayer(player.name)}
                      disabled={editRosterActivePlayers.length <= 4}
                    >
                      <PersonRemoveIcon fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
          </List>

          {/* Add new player */}
          <Box sx={{ mb: 2 }}>
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <TextField
                size="small"
                placeholder={translate('tournament.addPlayer')}
                value={editRosterNewPlayer}
                onChange={(e) => {
                  setEditRosterNewPlayer(e.target.value);
                  setEditRosterError('');
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleEditRosterAddPlayer();
                  }
                }}
                error={!!editRosterError}
                helperText={editRosterError}
                sx={{ flex: 1 }}
              />
              {tournamentData?.mixedPairs && (
                <FormControl size="small" sx={{ minWidth: 60 }}>
                  <TextField
                    select
                    size="small"
                    value={editRosterNewGender}
                    onChange={(e) => setEditRosterNewGender(e.target.value)}
                    SelectProps={{ native: true }}
                  >
                    <option value="male">M</option>
                    <option value="female">F</option>
                  </TextField>
                </FormControl>
              )}
              <IconButton color="primary" onClick={handleEditRosterAddPlayer}>
                <AddIcon />
              </IconButton>
            </Stack>
          </Box>

          {/* Inactive Players */}
          {editRosterPlayers.some((p) => p.status === 'inactive') && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                {translate('tournament.inactivePlayers')}
              </Typography>
              <List dense sx={{ bgcolor: 'action.hover', borderRadius: 1 }}>
                {editRosterPlayers
                  .filter((p) => p.status === 'inactive')
                  .map((player) => (
                    <ListItem key={player.name} sx={{ py: 0.5 }}>
                      <ListItemText
                        primary={player.name}
                        secondary={tournamentData?.mixedPairs ? (player.gender === 'male' ? 'M' : 'F') : undefined}
                        sx={{ opacity: 0.7 }}
                      />
                      <ListItemSecondaryAction>
                        <Button
                          size="small"
                          onClick={() => handleEditRosterReactivatePlayer(player.name)}
                        >
                          {translate('tournament.reactivatePlayer')}
                        </Button>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
              </List>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditRoster}>
            {translate('common.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleEditRosterApply}
            disabled={editRosterActivePlayers.length < 4}
          >
            {translate('tournament.applyChanges')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove Player Confirmation Dialog */}
      <Dialog
        open={removePlayerConfirm.open}
        onClose={handleRemovePlayerCancel}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{translate('tournament.removePlayerTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {translate('tournament.removePlayerMessage', { player: removePlayerConfirm.playerName })}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {translate('tournament.removePlayerExplanation')}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ flexDirection: 'column', gap: 1, p: 2 }}>
          <Button
            fullWidth
            variant="outlined"
            onClick={handleRemovePlayerMakeInactive}
          >
            {translate('tournament.makeInactive')}
          </Button>
          <Button
            fullWidth
            variant="contained"
            color="error"
            onClick={handleRemovePlayerCompletely}
          >
            {translate('tournament.removeCompletely')}
          </Button>
          <Button
            fullWidth
            onClick={handleRemovePlayerCancel}
          >
            {translate('common.cancel')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Final Confirmation Dialog for Complete Removal */}
      <Dialog
        open={removePlayerFinalConfirm.open}
        onClose={handleRemovePlayerFinalCancel}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{translate('tournament.removePlayerFinalTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            {translate('tournament.removePlayerFinalMessage', { player: removePlayerFinalConfirm.playerName })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRemovePlayerFinalCancel}>
            {translate('common.no')}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleRemovePlayerFinalConfirm}
          >
            {translate('common.yes')}
          </Button>
        </DialogActions>
      </Dialog>

  {/* Removed automatic end dialog; tournament continues until user ends manually */}
    </Box>
  );
}

export default Tournament;
